import React, { useState } from 'react';
import { Link } from 'react-router-dom';

interface PricingPlan {
  name: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  highlighted: boolean;
  cta: string;
}

const pricingPlans: PricingPlan[] = [
  {
    name: 'Free Trial',
    price: '$0',
    period: 'for 3 months',
    description: 'Full CimmpleFlow features. No credit card required.',
    features: [
      'All CimmpleFlow features included',
      'Quotations (VendorConnect™)',
      'Job Management (with live tracking)',
      'Quality Control (live reporting)',
      'Reporting (with scheduling)',
      'Accounting (advanced)',
      '2 users included',
      '50 orders limit',
      '1 GB storage limit',
    ],
    highlighted: false,
    cta: 'Start Free Trial',
  },
  {
    name: 'CimmpleFlow',
    price: '$49',
    period: '/user/month',
    description: 'Billed annually. $55/user if billed monthly.',
    features: [
      'Quotations (VendorConnect™)',
      'Order Management',
      'Purchasing',
      'Job Management (with live tracking)',
      'Quality Control (live reporting)',
      'Reporting (with scheduling)',
      'Accounting (advanced)',
      'Timesheets (3rd party integration)',
      'Document Storage (version control)',
      'Per-user pricing',
      'Unlimited Jobs/Customers',
    ],
    highlighted: true,
    cta: 'Get Started',
  },
];

const PricingSection: React.FC = () => {
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('yearly');

  return (
    <section id="pricing" className="py-24 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1 rounded-full bg-primary-100 dark:bg-primary-500/10 text-primary-700 dark:text-primary-400 text-sm font-medium mb-4">
            Pricing
          </span>
          <h2 className="section-title">
            Simple, transparent
            <br />
            <span className="gradient-text">pricing for everyone</span>
          </h2>
          <p className="section-subtitle mx-auto mt-4">
            Choose the plan that fits your needs. All plans include core features with no hidden fees.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {pricingPlans.map((plan, index) => (
            <div
              key={plan.name}
              className={`relative rounded-2xl p-8 transition-all duration-300 ${
                plan.highlighted
                  ? 'bg-gradient-to-b from-primary-100 dark:from-primary-500/10 to-white dark:to-dark-900/50 border-2 border-primary-500/50 scale-105 shadow-xl shadow-primary-500/10'
                  : 'card hover:border-slate-300 dark:hover:border-dark-600'
              }`}
              style={{ animationDelay: `${index * 100}ms` }}
            >

              {/* Plan Header */}
              <div className="text-center mb-8">
                {plan.name === 'CimmpleFlow' && (
                  <div className="mb-3">
                    <div className="inline-flex items-center p-1 rounded-full bg-slate-100 dark:bg-dark-800 border border-slate-200 dark:border-dark-700">
                      <button
                        onClick={() => setBillingPeriod('yearly')}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 ${
                          billingPeriod === 'yearly'
                            ? 'bg-primary-500 text-white'
                            : 'text-slate-600 dark:text-dark-300 hover:text-slate-900 dark:hover:text-white'
                        }`}
                      >
                        Yearly
                      </button>
                      <button
                        onClick={() => setBillingPeriod('monthly')}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 ${
                          billingPeriod === 'monthly'
                            ? 'bg-primary-500 text-white'
                            : 'text-slate-600 dark:text-dark-300 hover:text-slate-900 dark:hover:text-white'
                        }`}
                      >
                        Monthly
                      </button>
                    </div>
                  </div>
                )}
                <h3 className="text-xl font-display font-bold text-slate-900 dark:text-white mb-2">
                  {plan.name}
                </h3>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-display font-bold text-slate-900 dark:text-white">
                    {plan.name === 'CimmpleFlow' ? (billingPeriod === 'yearly' ? '$49' : '$55') : plan.price}
                  </span>
                  <span className="text-slate-500 dark:text-dark-400">{plan.period}</span>
                </div>
                <p className="text-slate-500 dark:text-dark-400 mt-2 text-sm">
                  {plan.name === 'CimmpleFlow' 
                    ? (billingPeriod === 'yearly' 
                        ? 'Billed annually. $55/user if billed monthly.' 
                        : 'Billed monthly. Save with annual billing.')
                    : plan.description}
                </p>
              </div>

              {/* Features */}
              <ul className="space-y-4 mb-8">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-700 dark:text-dark-300">
                    <svg
                      className={`w-5 h-5 flex-shrink-0 ${
                        plan.highlighted ? 'text-primary-600 dark:text-primary-400' : 'text-green-600 dark:text-green-500'
                      }`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <Link
                to="/contact"
                className={`block text-center w-full py-3 rounded-xl font-semibold transition-all duration-300 ${
                  plan.highlighted
                    ? 'btn-primary'
                    : 'btn-secondary'
                }`}
              >
                {plan.cta}
              </Link>
            </div>
          ))}
        </div>

        {/* CimmplePay Add-on */}
        <div className="mt-16 p-8 rounded-2xl bg-gradient-to-r from-accent-100 dark:from-accent-500/10 to-white dark:to-dark-900/50 border border-accent-300 dark:border-accent-500/30">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-accent-200 dark:bg-accent-500/20 flex items-center justify-center">
                <svg className="w-7 h-7 text-accent-600 dark:text-accent-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-display font-bold text-slate-900 dark:text-white">Need CimmplePay?</h3>
                <p className="text-slate-600 dark:text-dark-300">Our upcoming payroll solution starting at just <span className="text-accent-600 dark:text-accent-400 font-semibold">$25/month</span></p>
                <span className="inline-block mt-2 px-3 py-1 rounded-full bg-accent-100 dark:bg-accent-500/20 text-accent-700 dark:text-accent-400 text-xs font-medium">
                  Coming Soon
                </span>
              </div>
            </div>
            <Link to="/pricing?product=cimmlepay" className="px-6 py-3 bg-accent-100 dark:bg-accent-500/20 border border-accent-300 dark:border-accent-500/50 text-accent-700 dark:text-accent-400 font-semibold rounded-xl hover:bg-accent-200 dark:hover:bg-accent-500/30 transition-all duration-300">
              View CimmplePay Pricing
            </Link>
          </div>
        </div>

        {/* FAQ Link */}
        <div className="text-center mt-12">
          <p className="text-slate-600 dark:text-dark-400">
            Have questions?{' '}
            <Link to="/contact" className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium">
              Contact our team
            </Link>
          </p>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
